import {
	DropdownOption,
	RadioDropdown,
} from '@qr-front/ui/dashboard/components/RadioDropdown'
import { SelectDropdown } from '@qr-front/ui/dashboard/components/SelectDropdown'
import { cn } from '@qr-front/ui/utils'
import { Table } from '@tanstack/react-table'
import { useLocale, useTranslations } from 'next-intl'

import { TQrData } from '@/generator/shared'

import { disabledColDef, previewColDef } from '../columnsConfig'
import {
	getQrTypeOptions,
	getSortOptions,
	getSortValueFromState,
	getStatusOptions,
	parseSortValue,
} from './filterOptions'

type SortOption = DropdownOption<{ desc: boolean }>

export const DesktopFilters = ({
	table,
	onClear,
}: {
	table: Table<TQrData>
	onClear?: () => void
}) => {
	const t = useTranslations()
	const locale = useLocale()

	const qrTypeOptions = getQrTypeOptions(t)
	const statusOptions = getStatusOptions(t)
	const sortOptions: SortOption[] = getSortOptions(t)

	const hasAppliedFilters =
		table.getState().columnFilters.length > 0 ||
		table.getState().sorting.length > 0

	return (
		<div className="-mb-4 hidden h-14 flex-1 items-start gap-4 overflow-x-auto desktop:flex">
			<SelectDropdown
				placeholder={t('dashboard__qr_table__controls__status_placeholder')}
				options={statusOptions}
				selected={
					(table.getColumn(disabledColDef.id!)?.getFilterValue() as string[]) ??
					[]
				}
				onChange={(value) =>
					table
						.getColumn(disabledColDef.id!)
						?.setFilterValue(value.length > 0 ? value : undefined)
				}
				className="w-fit"
				triggerClassName="min-w-[157px] max-w-fit"
			/>
			<SelectDropdown
				placeholder={t('dashboard__qr_table__controls__type_placeholder')}
				options={qrTypeOptions}
				onChange={(value) =>
					table
						.getColumn(previewColDef.id!)
						?.setFilterValue(value.length > 0 ? value : undefined)
				}
				selected={
					(table.getColumn(previewColDef.id!)?.getFilterValue() as string[]) ??
					[]
				}
				className="w-fit"
				triggerClassName={cn(
					'min-w-64 max-w-fit',
					// this locales have longer dropdown labels
					['sk', 'sl', 'el'].includes(locale) && 'min-w-72',
				)}
			/>
			<RadioDropdown
				placeholder={t('dashboard__qr_table__controls__sort_placeholder')}
				options={sortOptions}
				selected={getSortValueFromState(
					table.getState().sorting[0]?.id,
					table.getState().sorting[0]?.desc,
				)}
				onChange={(option) => {
					if (!option.value) {
						table.setSorting([])
						return
					}
					const parsed = parseSortValue(option.value)
					if (parsed) table.setSorting([parsed])
				}}
				className="w-fit"
				triggerClassName="min-w-[187px] max-w-fit"
			/>
			<div className="grow" />
			{hasAppliedFilters && (
				<button
					onClick={() => {
						table.resetColumnFilters()
						table.resetSorting()
						onClear?.()
					}}
					disabled={!hasAppliedFilters}
					className="mt-2 flex cursor-pointer items-center justify-end gap-2 transition-opacity disabled:cursor-default disabled:text-grey disabled:opacity-50 hover:opacity-50"
				>
					<span className="type-body-small whitespace-nowrap text-blue underline">
						{t('dashboard__qr_table__controls__clear_filters')}
					</span>
				</button>
			)}
		</div>
	)
}
